import React, { useState } from 'react';
import './App.css';
import JTBDForm from './components/JTBDForm';
import Atom from './components/Atom';
import logo from './logo.svg';
import sampleData from './sample-data/marketing-notes.json';
import { FileText, Image, Link } from 'lucide-react';

function App() {
  const [currentJob, setCurrentJob] = useState({actor: "marketing manager", action: "create a marketing campaign", object: "marketing campaign", target: "B2B SaaS product", intention: "increase conversions by 25%", fullStatement: "As a marketing manager, I need to create a marketing campaign for our B2B SaaS product to increase conversions by 25%."});
  const [showForm, setShowForm] = useState(true);
  const [userRole] = useState('marketing manager');

  const handleJobDefined = (jobData) => {
    setCurrentJob(jobData);
    setShowForm(false);
  };

  const handleBackToForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  // Generate contextual advertisement based on current job
  const getContextualAd = () => {
    if (!currentJob) return null;

    return {
      title: "📊 Market Research Pro",
      description: "Get comprehensive market insights and competitor analysis",
      cta: "Start Research",
      type: "research"
    };
  };

  const contextualAd = getContextualAd();

  return (
    <div className="App">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-left">
          {currentJob && (
            <button className="back-button" onClick={handleBackToForm}>← Back</button>
          )}
          <div className="app-logo">
            <img src={logo} alt="meos" />
          </div>
          <div className="job-info">
            <div className="job-statement">
              {currentJob.fullStatement}
            </div>
          </div>
        </div>
        <div className="job-metadata">
          {currentJob && (
            <>
              <div className="user-role">{userRole}</div>
            </>
          )}
        </div>
      </div>

      {/* Main Workbench */}
      <div className="workbench">
        {/* Left Sidebar - Quick Capture */}
        <div className="sidebar">
          <div className="sidebar-content">
            {/* Main content area - can be used for other sidebar content */}
          </div>

          {/* Quick Capture Icons - Bottom Left */}
          <div className="quick-capture">
            <div className="capture-icon-btn" title="Create Note">
              <FileText size={20} />
            </div>
            <div className="capture-icon-btn" title="Add Image">
              <Image size={20} />
            </div>
            <div className="capture-icon-btn" title="Save Link">
              <Link size={20} />
            </div>
          </div>
        </div>

        {/* Left Panel - Materials & Curations */}
        <div className="panel left-panel">
          <div className="panel-header">
            <h2>Materials & Curations</h2>
            <p>Notes, images, and links from your research</p>
          </div>

          <div className="atoms-container">
            {sampleData.atoms.map((atom) => (
              <Atom key={atom.nexus.id} atom={atom} />
            ))}
          </div>

          <div className="add-materials-section">
            <button className="add-materials-btn">+ Add Materials</button>
          </div>
        </div>

        {/* Middle Panel - AI Assistant */}
        <div className="panel middle-panel">
          <div className="panel-header">
            <h2>AI Assistant</h2>
            <p>Your guided companion for completing this job</p>
          </div>

          <div className="chat-area">
                <div className="ai-message">
                  <div className="ai-avatar">
                    <img src={logo} alt="meos" />
                  </div>
                  <div className="message-content">
                    I'm here to help you {currentJob.action} {currentJob.object} for {currentJob.target}. Let me break this down into actionable steps and gather the resources you'll need.
                  </div>
                </div>

                <div className="user-message">
                  <div className="message-content">
                    I need to create a marketing campaign for our B2B SaaS product. We're targeting mid-market companies and want to increase conversions by 25%.
                  </div>
                </div>

                <div className="ai-message">
                  <div className="ai-avatar">
                    <img src={logo} alt="meos" />
                  </div>
                  <div className="message-content">
                    Perfect! Let's build a comprehensive B2B marketing campaign. I've identified your key challenges and opportunities. Here's what I recommend we focus on:
                    <br /><br />
                    <strong>🎯 Campaign Strategy Framework:</strong>
                    <br />• Define ICP (Ideal Customer Profile) and pain points
                    <br />• Create multi-channel approach (LinkedIn, email, content)
                    <br />• Develop lead magnet and nurture sequence
                    <br />• Set up conversion tracking and analytics
                    <br /><br />
                    <strong>📊 Key Metrics to Track:</strong>
                    <br />• Lead quality score
                    <br />• Conversion rate by channel
                    <br />• Cost per acquisition (CPA)
                    <br />• Customer lifetime value (CLV)
                  </div>
                </div>

                <div className="user-message">
                  <div className="message-content">
                    Can you help me research our competitors and see what's working in our space?
                  </div>
                </div>

                <div className="ai-message">
                  <div className="ai-avatar">
                    <img src={logo} alt="meos" />
                  </div>
                  <div className="message-content">
                    Absolutely! I've already started gathering competitive intelligence. I found some great insights:
                    <br /><br />
                    <strong>🔍 Competitive Analysis:</strong>
                    <br />• Competitor A uses LinkedIn thought leadership (high engagement)
                    <br />• Competitor B focuses on case studies (converts 3x better)
                    <br />• Competitor C uses webinars (generates 40% of leads)
                    <br /><br />
                    <strong>💡 Opportunity Gap:</strong>
                    <br />• Most competitors aren't using video testimonials
                    <br />• Limited personalisation in email sequences
                    <br />• No one's doing account-based marketing effectively
                    <br /><br />
                    I've saved these findings to your Materials panel for reference. Want me to dive deeper into any of these areas?
                  </div>
                </div>

                <div className="suggested-steps">
                  <h4>Suggested Next Steps</h4>
                  <div className="suggested-buttons">
                    <button className="suggested-btn">
                      Research best practices for {currentJob.object}
                    </button>
                    <button className="suggested-btn">
                      Identify key stakeholders and requirements
                    </button>
                    <button className="suggested-btn">
                      Create a project timeline and milestones
                    </button>
                    <button className="suggested-btn">
                      Gather relevant case studies and examples
                    </button>
                  </div>
                </div>
          </div>

          <div className="chat-input">
            <input
              type="text"
              className="chat-input-field"
              placeholder="Ask me anything about your job..."
            />
            <button className="chat-send-btn">→</button>
          </div>
        </div>

        {/* Right Panel - Research & Tools */}
        <div className="panel right-panel">
          <div className="panel-header">
            <h2>Research & Tools</h2>
            <p>Search and discover credible & relevant resources</p>
          </div>

          <div className="search-area">
            <div className="search-input-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search for resources..."
              />
              <button className="search-btn">🔍</button>
            </div>
          </div>

          <div className="search-results">
            <h4>Recent Searches</h4>
            <div className="result-card">
              <h5>Marketing Strategy Best Practices</h5>
              <div className="result-meta">Harvard Business Review • 5 min read</div>
              <p className="result-description">Comprehensive guide to developing effective marketing strategies for B2B companies.</p>
            </div>
            <div className="result-card">
              <h5>Digital Marketing Trends 2024</h5>
              <div className="result-meta">Forrester Research • 8 min read</div>
              <p className="result-description">Latest trends and predictions for digital marketing in the coming year.</p>
            </div>
          </div>
        </div>
      </div>

      {/* JTBD Form Popup */}
      {showForm && (
        <JTBDForm onJobDefined={handleJobDefined} onClose={handleCloseForm} />
      )}

      {/* Contextual Advertising Spot */}
      {contextualAd && (
        <div className="contextual-ad-spot">
          <h3>{contextualAd.title}</h3>
          <p>{contextualAd.description}</p>
          <button>{contextualAd.cta}</button>
        </div>
      )}
    </div>
  );
}

export default App;
